import { Box, lighten, useTheme } from '@material-ui/core';
import _, { max } from 'lodash';
import * as React from 'react';
import { useEffect } from 'react';
import BarChart from '../../../../components/muiLegacy/Charts/BarChart';
import AnalysisChartLoading from '../../AnalysisChartLoading';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { ChartProps } from '../../AnalysisList';
import { internationalText } from '../../internationalText';
import DataTimedOut, { didQueryTimeout } from '../DataTimedOut';
import NotEnoughData from '../NotEnoughData';
import NotEnoughTagGroups from '../NotEnoughTagGroups';

export interface TopicalCoverageProps extends ChartProps {}

const TopicalCoverage: React.FC<TopicalCoverageProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
}) => {
  // Adjust shown filters
  useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: { showTagGroupIds: true, showTargetRankValues: true },
      })),
    []
  );
  // Fetch data
  const topicalCoverageQuery = projectsApiService.queries.analysis.useGetTopicalCoverage({
    projectId: project.id,
    tagGroupIds: analysisFilterState?.tagGroupIds,
    rankValues: analysisFilterState?.targetRankValues,
  });

  const topicalCoverageLoading = topicalCoverageQuery?.query?.loading;
  const topicalCoverageData = topicalCoverageQuery?.result?.tagGroupAnalyses;

  // Structure data
  const chartData = _.chain(
    topicalCoverageData
      ?.filter((tg) => analysisFilterState?.tagGroupIds.includes(tg.tagGroupId))
      .map((tg): any => ({
        id: tg.tagGroupId,
        name: tg.tagGroupName,
        [internationalText.chart.market_potential_by_topic.current_traffic[language]]:
          tg.searchTraffic,
        potentialSearchTraffic: tg.potentialSearchTraffic,
        [internationalText.chart.market_potential_by_topic.traffic_increase[language]]:
          tg.potentialSearchTraffic <= tg.searchTraffic
            ? 0
            : tg.potentialSearchTraffic - tg.searchTraffic,
      }))
  )
    .orderBy((tg) => tg.potentialSearchTraffic, ['asc'])
    .value();

  // Styling
  const maxLength = max(chartData.map((value) => value.name.length)) ?? 0;
  const rawScaleFactor = maxLength / 36;
  const leftScaleFactor = Math.max(Math.min(0.9, rawScaleFactor), 0.6);
  const theme = useTheme();
  const yPadding = theme.spacing(12);
  const xPaddingTotal = theme.spacing(40);
  const xPaddingLeft = xPaddingTotal * leftScaleFactor;
  const xPaddingRight = xPaddingTotal * (1 - leftScaleFactor);

  // Render
  if (topicalCoverageLoading) {
    return <AnalysisChartLoading />;
  }

  if (!analysisFilterState?.tagGroupIds?.length) {
    return <NotEnoughTagGroups />;
  }

  if (didQueryTimeout(topicalCoverageQuery.query)) {
    return <DataTimedOut />;
  }

  if (!topicalCoverageData?.some((tg) => tg.searchTraffic > 0 && tg.potentialSearchTraffic > 0)) {
    return <NotEnoughData />;
  }

  return (
    <div className="min-h-[500px] w-full">
      <BarChart
        segmentLegend
        palette={palette}
        height={yPadding * 2 + chartData.length * 50 + 60}
        ResponsiveBarProps={{
          data: chartData,
          layout: 'horizontal',
          keys: [
            internationalText.chart.market_potential_by_topic.current_traffic[language],
            internationalText.chart.market_potential_by_topic.traffic_increase[language],
          ],
          colors: [palette.primaryScale[1], palette.primaryScale[2]],
          enableGridY: false,
          enableGridX: true,
          borderColor: '#fff',
          indexBy: 'id',
          margin: {
            top: yPadding + 60,
            bottom: yPadding,
            right: xPaddingRight,
            left: xPaddingLeft,
          },
          axisLeft: {
            format: (v: any) => chartData?.find((x) => x.id === v)?.name,
            tickSize: 0,
            tickPadding: 10,
            tickRotation: 0,
          },
          axisTop: {
            format: '~s',
            tickSize: 0,
            tickPadding: 5,
            tickRotation: 0,
            legend: internationalText.chart.market_potential_by_topic.traffic[language],
            legendPosition: 'middle',
            legendOffset: -50,
          },
          axisBottom: null,
          legends: [
            {
              dataFrom: 'keys',
              anchor: 'top-left',
              direction: 'row',
              itemWidth: 200,
              itemHeight: 20,
              itemsSpacing: 20,
              translateY: -120,
              itemDirection: 'left-to-right',
            },
          ],
        }}
      />
    </div>
  );
};

export default TopicalCoverage;
